<template>
  <!-- eslint-disable vue/no-mutating-props -->
  <el-date-picker
    v-model="model[prop]"
    v-bind="innerAttrs"
    :type="pickerType"
    range-separator="至"
    start-placeholder="开始日期"
    end-placeholder="结束日期"
    style="width: 100%"
    :value-format="valueFormat"
    :placeholder="innerAttrs.placeholder || '选择日期'"
    :disabled="disabled"
    :disabled-date="innerDisabledDateFn"
  />
</template>

<script lang="ts">
export default { inheritAttrs: false }
</script>

<script setup lang="ts">
import { dateUtil, watch, computed } from '@zto/zpage'

import { useFormItem } from '../util'

import type { GenericFunction } from '@zto/zpage'

const props = withDefaults(
  defineProps<{
    model: Record<string, any>
    prop: string
    pickerType?: string
    valueFormat?: string
    disabled?: boolean
    beforeToday?: boolean
    afterToday?: boolean
    minDate?: string | Date
    maxDate?: string | Date

    disabledDate?: Function
    disabledHours?: Function // 此功能影响用户体验暂不支持
    disabledMinutes?: Function // 此功能影响用户体验暂不支持
    disabledSeconds?: Function // 此功能影响用户体验暂不支持

    onChange?: GenericFunction
  }>(),
  {
    pickerType: 'date',
    beforeToday: true,
    valueFormat: 'YYYY-MM-DD HH:mm:ss',
    disabled: false
  }
)

const { innerAttrs } = useFormItem(props)

/** 日期时间选择需要特殊处理 */
const isDateTimePicker = computed(() => {
  return props.pickerType === 'datetime'
})

watch(
  () => props.model[props.prop],
  (cur, old) => {
    if (cur && cur !== old) {
      let val = dateUtil.parse(props.model[props.prop])
      props.model[props.prop] = dateUtil.format(val, props.valueFormat)
    }
  }
)

const parsedMinDate = computed(() => {
  if (!props.minDate) return null
  return dateUtil.parse(props.minDate) as Date
})

const parsedMaxDate = computed(() => {
  if (!props.maxDate) return null
  return dateUtil.parse(props.maxDate) as Date
})

const formattedDate = computed(() => {
  return dateUtil.parseAndFormat(props.model[props.prop], 'YYYY-MM-DD')
})

const formattedNow = computed(() => {
  return dateUtil.parseAndFormat(new Date(), 'YYYY-MM-DD')
})

function innerDisabledDateFn(time: any) {
  if (props.disabledDate && props.disabledDate(time)) return true

  // beforeToday与afterToday互斥
  const beforeToday = props.beforeToday && props.afterToday !== true

  if (beforeToday && time > dateUtil.endOf(new Date())) return true
  if (props.afterToday && time < dateUtil.startOf(new Date())) return true

  if (parsedMaxDate.value && time > dateUtil.endOf(parsedMaxDate.value).getTime()) return true
  if (parsedMinDate.value && time < dateUtil.startOf(parsedMinDate.value).getTime()) return true

  return false
}

const innerDisabledHoursFn = (day: number) => {
  if (props.disabledHours && props.disabledHours(day)) return true
  if (!isDateTimePicker.value) return false

  // 比较选择日期和当前日期，如果相等，则禁止某段的小时，不相等，不禁止
  if (formattedDate.value !== formattedNow.value) return false

  let min = 0 // 最小小时数
  let max = 23 // 最大小时数

  if (parsedMinDate.value) min = parsedMinDate.value.getHours()
  if (parsedMaxDate.value) max = parsedMaxDate.value.getHours()

  const minRange = makeTimeRange(-1, min - 1)
  const maxRange = makeTimeRange(max + 1, 24)

  console.log([...minRange, ...maxRange])

  return [...minRange, ...maxRange]
}

function innerDisabledMinutesFn(hour: number) {
  if (props.disabledMinutes && props.disabledMinutes(hour)) return true
  if (!isDateTimePicker.value) return false
  if (formattedDate.value !== formattedNow.value) return false

  let min = 0 // 最小分钟数
  let max = 59 // 最大分钟数

  if (parsedMinDate.value && hour === parsedMinDate.value.getHours()) min = parsedMinDate.value.getMinutes()
  if (parsedMaxDate.value && hour === parsedMaxDate.value.getHours()) max = parsedMaxDate.value.getMinutes()

  const minRange = makeTimeRange(-1, min - 1)
  const maxRange = makeTimeRange(max + 1, 60)

  return [...minRange, ...maxRange]
}

function innerDisabledSecondsFn(hour: number, minute: number) {
  if (props.disabledSeconds && props.disabledSeconds(hour, minute)) return true
  if (!isDateTimePicker.value) return false
  if (formattedDate.value !== formattedNow.value) return false

  let min = 0 // 最小秒数
  let max = 59 // 最大秒数

  if (parsedMinDate.value && hour === parsedMinDate.value.getHours() && minute === parsedMinDate.value.getMinutes())
    min = parsedMinDate.value.getSeconds()
  if (parsedMaxDate.value && hour === parsedMaxDate.value.getHours() && minute === parsedMaxDate.value.getMinutes())
    max = parsedMaxDate.value.getSeconds()

  const minRange = makeTimeRange(-1, min - 1)
  const maxRange = makeTimeRange(max + 1, 60)

  return [...minRange, ...maxRange]
}

const makeTimeRange = (start: number, end: number) => {
  const result: number[] = []
  for (let i = start; i <= end; i++) {
    result.push(i)
  }
  return result
}
</script>
